import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AUTH_PROVIDERS, isFigmaEmbedAllowedOrigin } from '@/consts/auth.consts';
import { useAuthConfig } from '@/hooks/useAuthConfig';

/**
 * Embed-mode session state: whether we're running inside Figma's embed
 * iframe, plus any permission flags the parent pushes in over postMessage.
 *
 * Child components should call `useIsFigmaEmbedMode()` rather than checking
 * `useAuthConfig().provider === AUTH_PROVIDERS.FIGMA_EMBED` themselves — the
 * provider here centralizes that derivation.
 *
 * `canCreateWeaveFile` mirrors the boolean Figma resolves from
 * `useFileCreationPermissions` (LiveGraph, drafts folder) and posts via
 * `WEAVE_PERMISSIONS_REFRESH`. `null` means we haven't heard from the parent
 * yet — surfaces should treat it as "disabled, still loading" rather than
 * default-allow.
 */
interface FigmaEmbedModeContextValue {
  isFigmaEmbedMode: boolean;
  canCreateWeaveFile: boolean | null;
}

const DEFAULT: FigmaEmbedModeContextValue = {
  isFigmaEmbedMode: false,
  canCreateWeaveFile: null,
};

const FigmaEmbedModeContext = createContext<FigmaEmbedModeContextValue>(DEFAULT);

const PERMISSIONS_REFRESH_TYPE = 'WEAVE_PERMISSIONS_REFRESH';
const PERMISSIONS_REQUEST_TYPE = 'WEAVE_PERMISSIONS_REQUEST';

export function FigmaEmbedModeProvider({ children }: { children: ReactNode }) {
  const { provider } = useAuthConfig();
  const isFigmaEmbedMode = useMemo(() => provider === AUTH_PROVIDERS.FIGMA_EMBED, [provider]);
  const [canCreateWeaveFile, setCanCreateWeaveFile] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isFigmaEmbedMode) return undefined;

    const handleMessage = (event: MessageEvent) => {
      if (!isFigmaEmbedAllowedOrigin(event.origin)) return;
      const data = event.data as { type?: unknown; canCreateWeaveFile?: unknown } | null;
      if (!data || data.type !== PERMISSIONS_REFRESH_TYPE) return;
      if (typeof data.canCreateWeaveFile !== 'boolean') return;
      setCanCreateWeaveFile(data.canCreateWeaveFile);
    };
    window.addEventListener('message', handleMessage);

    // Ask the parent for the current value on mount. The parent also pushes
    // proactively whenever the value changes, so this is just to cover the
    // case where permissions resolved on the parent before we mounted.
    if (window.parent !== window) {
      window.parent.postMessage({ type: PERMISSIONS_REQUEST_TYPE }, '*');
    }

    return () => window.removeEventListener('message', handleMessage);
  }, [isFigmaEmbedMode]);

  const value = useMemo(() => ({ isFigmaEmbedMode, canCreateWeaveFile }), [isFigmaEmbedMode, canCreateWeaveFile]);

  return <FigmaEmbedModeContext.Provider value={value}>{children}</FigmaEmbedModeContext.Provider>;
}

export function useIsFigmaEmbedMode(): boolean {
  return useContext(FigmaEmbedModeContext).isFigmaEmbedMode;
}

export function useFigmaEmbedPermissions(): { canCreateWeaveFile: boolean | null } {
  const { canCreateWeaveFile } = useContext(FigmaEmbedModeContext);
  return { canCreateWeaveFile };
}
